// === grandmaster-frontend/frontend/src/App.js ===
// (REVISED - Removed notifyExtension function and calls)
import React, { useState, useEffect, createContext, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import Home from "./components/Home";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Login from "./components/Login";
import LichessCallback from "./components/LichessCallback";
import Dashboard from "./components/Dashboard";
import ChromeExtensionUsage from "./components/ChromeExtensionUsage";
import PricingPage from "./components/PricingPage";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
// Assuming Header is rendered within specific pages like Home, Dashboard etc.
import "./App.css";
import {
  trackPageView,
  registerClickTracking,
  registerScrollDepth,
  registerTimeOnPage,
  registerFormTracking,
  registerErrorTracking,
  trackEvent,
} from "./utils/mixpanel";

export const AuthContext = createContext(null);

// Define API base URL based on environment
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8000"; // Local backend
  } else {
    return "https://api.grandmasterai.com"; // Production backend
  }
};
export const API_BASE_URL = getApiBaseUrl();

// REMOVED the notifyExtension helper function

// Wrap on location change
function TrackPageViews({ children }) {
  const location = useLocation();
  useEffect(() => {
    trackEvent("Page View", { path: location.pathname });
  }, [location]);
  return children;
}

function App() {
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  // once at mount:
  useEffect(() => {
    registerClickTracking();
    registerScrollDepth();
    registerTimeOnPage();
    registerFormTracking();
    registerErrorTracking();
  }, []);

  // Function to fetch user data using the current token
  const fetchUser = useCallback(async (token) => {
    if (!token) {
      setUser(null);
      setLoadingAuth(false);
      // REMOVED Extension Notification
      return;
    }
    setLoadingAuth(true);
    try {
      console.log("Fetching user with token:", token ? "present" : "absent");
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const userData = await response.json();
        console.log("User data fetched successfully:", userData);
        setUser(userData);
        localStorage.setItem("authToken", token);
        setAuthToken(token);
        // REMOVED Extension Notification
      } else {
        console.log("Failed to fetch user or token invalid, clearing state.");
        localStorage.removeItem("authToken");
        setAuthToken(null);
        setUser(null);
        // REMOVED Extension Notification
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUser(null);
      // REMOVED Extension Notification
    } finally {
      setLoadingAuth(false);
    }
  }, []);

  // Function to save token and trigger user fetch
  const saveTokenAndFetchUser = useCallback(
    (token) => {
      if (token) {
        localStorage.setItem("authToken", token);
        setAuthToken(token);
        fetchUser(token);
      } else {
        localStorage.removeItem("authToken");
        setAuthToken(null);
        setUser(null);
        setLoadingAuth(false);
        // REMOVED Extension Notification
      }
    },
    [fetchUser]
  );

  // Initial load effect
  useEffect(() => {
    const initialToken = localStorage.getItem("authToken");
    console.log("Initial token check:", initialToken ? "found" : "not found");
    fetchUser(initialToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleLogout = useCallback(() => {
    saveTokenAndFetchUser(null); // Clear token and user state
  }, [saveTokenAndFetchUser]);

  if (loadingAuth) {
    return (
      <div
        className="center"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          verticalAlign: "middle",
        }}
      >
        ...
      </div>
    );
  }

  return (
    // Provide saveTokenAndFetchUser as saveToken for simplicity
    <AuthContext.Provider
      value={{
        authToken,
        user,
        saveToken: saveTokenAndFetchUser,
        handleLogout,
        fetchUser,
        setUser,
      }}
    >
      <Router>
        <TrackPageViews>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route
              path="/chrome-extension"
              element={<ChromeExtensionUsage />}
            />
            <Route
              path="/login"
              element={!authToken ? <Login /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="/auth-lichess-callback"
              element={<LichessCallback />}
            />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route
              path="/dashboard"
              element={
                authToken ? (
                  <Dashboard />
                ) : (
                  <Navigate to="/login?redirect=/dashboard" />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </TrackPageViews>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
